import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  TablePagination,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as ApproveIcon,
  Cancel as UnapproveIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  PendingActions as PendingActionsIcon,
  Done as DoneIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { useAppSelector } from '../hooks/redux.hooks';
import { useConfirm } from '../hooks/useConfirm';
import { custodySettlementService } from '../services/custodySettlements.service';
import apiClient from '../services/api';
import { CustodySettlement } from '../types/custodySettlements.types';
import CustodySettlementDialog from '../components/custodySettlements/CustodySettlementDialog';
import CustodySettlementPreviewDialog from '../components/custodySettlements/CustodySettlementPreviewDialog';

const translations = {
  en: {
    title: 'Custody Settlements',
    newSettlement: 'New Settlement',
    status: 'Status',
    allStatuses: 'All Statuses',
    allBranches: 'All Branches',
    draft: 'Draft',
    approved: 'Approved',
    posted: 'Posted',
    settlementNumber: 'Settlement #',
    custody: 'Custody',
    settlementDate: 'Date',
    totalExpenses: 'Total Expenses',
    returnedAmount: 'Returned',
    branch: 'Branch',
    actions: 'Actions',
    view: 'View Details',
    edit: 'Edit',
    approve: 'Approve',
    unapprove: 'Unapprove',
    delete: 'Delete',
    print: 'Print',
    search: 'Search by settlement number or custody...',
    loading: 'Loading...',
    noData: 'No settlements found',
    refresh: 'Refresh',
    totalSettlements: 'Total Settlements',
    draftSettlements: 'Draft',
    approvedSettlements: 'Approved',
    rowsPerPage: 'Rows per page:',
    deleteConfirm: 'Are you sure you want to delete this settlement?',
    approveConfirm: 'Are you sure you want to approve this settlement? A journal entry will be created.',
    unapproveConfirm: 'Are you sure you want to unapprove? The journal entry will be deleted.',
    deleteSuccess: 'Settlement deleted successfully',
    approveSuccess: 'Settlement approved successfully',
    unapproveSuccess: 'Settlement unapproved successfully',
    deleteFailed: 'Failed to delete settlement',
    approveFailed: 'Failed to approve settlement',
    unapproveFailed: 'Failed to unapprove settlement',
    kwd: 'KWD',
    noPermission: 'You do not have permission for this action',
    fromDate: 'From Date',
    toDate: 'To Date',
  },
  ar: {
    title: 'كشوف تسوية العُهد',
    newSettlement: 'كشف تسوية جديد',
    status: 'الحالة',
    allStatuses: 'جميع الحالات',
    allBranches: 'جميع الفروع',
    draft: 'مسودة',
    approved: 'معتمد',
    posted: 'مرحّل',
    settlementNumber: 'رقم الكشف',
    custody: 'العُهدة',
    settlementDate: 'التاريخ',
    totalExpenses: 'إجمالي المصروفات',
    returnedAmount: 'المرتجع',
    branch: 'الفرع',
    actions: 'الإجراءات',
    view: 'عرض التفاصيل',
    edit: 'تعديل',
    approve: 'اعتماد',
    unapprove: 'إلغاء الاعتماد',
    delete: 'حذف',
    print: 'طباعة',
    search: 'البحث برقم الكشف أو العُهدة...',
    loading: 'جاري التحميل...',
    noData: 'لا توجد كشوف تسوية',
    refresh: 'تحديث',
    totalSettlements: 'إجمالي الكشوف',
    draftSettlements: 'مسودات',
    approvedSettlements: 'معتمدة',
    rowsPerPage: 'عدد الصفوف:',
    deleteConfirm: 'هل أنت متأكد من حذف هذا الكشف؟',
    approveConfirm: 'هل أنت متأكد من اعتماد هذا الكشف؟ سيتم إنشاء قيد محاسبي.',
    unapproveConfirm: 'هل أنت متأكد من إلغاء الاعتماد؟ سيتم حذف القيد المحاسبي.',
    deleteSuccess: 'تم حذف الكشف بنجاح',
    approveSuccess: 'تم اعتماد الكشف بنجاح',
    unapproveSuccess: 'تم إلغاء اعتماد الكشف بنجاح',
    deleteFailed: 'فشل حذف الكشف',
    approveFailed: 'فشل اعتماد الكشف',
    unapproveFailed: 'فشل إلغاء اعتماد الكشف',
    kwd: 'د.ك',
    noPermission: 'ليس لديك صلاحية لهذا الإجراء',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
  },
};

const CustodySettlementsPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const user = useAppSelector((state) => state.auth.user);
  const permissions = useAppSelector((state) => state.auth.permissions);

  const hasPermission = (perm: string): boolean => {
    if (user?.isSuperAdmin) return true;
    return permissions.includes(perm);
  };

  const [settlements, setSettlements] = useState<CustodySettlement[]>([]);
  const [filteredSettlements, setFilteredSettlements] = useState<CustodySettlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [branchFilter, setBranchFilter] = useState<string>('');
  const [branches, setBranches] = useState<{branchId: number, branchName: string, branchNameAr?: string}[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState<CustodySettlement | null>(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [printSettlement, setPrintSettlement] = useState<CustodySettlement | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const confirm = useConfirm();
  const [stats, setStats] = useState({ total: 0, draft: 0, approved: 0 });

  useEffect(() => { loadSettlements(); }, [statusFilter, fromDate, toDate]);
  useEffect(() => { apiClient.get('/Branches').then(r => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(() => { applyFilters(); calculateStats(); }, [searchTerm, branchFilter, settlements]);

  const loadSettlements = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      const response = await custodySettlementService.getAll(params);
      setSettlements(response.data);
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.error(t.noPermission, { position: 'top-center', autoClose: 3000, style: { fontSize: '16px', fontWeight: 'bold' } });
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = settlements;
    if (branchFilter) {
      filtered = filtered.filter((s) => s.branchName === branchFilter || s.branchNameAr === branchFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((s) =>
        s.settlementNumber?.toLowerCase().includes(term) ||
        (s as any).custodyNumber?.toLowerCase().includes(term) ||
        s.custodyName?.toLowerCase().includes(term) ||
        s.custodyNameAr?.toLowerCase().includes(term)
      );
    }
    setFilteredSettlements(filtered);
    setPage(0);
  };

  const calculateStats = () => {
    setStats({
      total: settlements.length,
      draft: settlements.filter((s) => s.status === 'Draft').length,
      approved: settlements.filter((s) => s.status === 'Approved').length,
    });
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.deleteConfirm, severity: 'error' });
    if (!ok) return;
    try {
      await custodySettlementService.delete(id);
      toast.success(t.deleteSuccess, { position: 'top-center', autoClose: 2000, style: { fontSize: '16px', fontWeight: 'bold' } });
      loadSettlements();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.response?.data || t.deleteFailed;
      toast.error(typeof msg === 'string' ? msg : t.deleteFailed, { position: 'top-center', autoClose: 5000, style: { fontSize: '16px', fontWeight: 'bold' } });
    }
  };

  const handleApprove = async (id: number) => {
    if (actionLoading) return;
    const ok = await confirm({ message: t.approveConfirm, severity: 'warning' });
    if (!ok) return;
    try {
      setActionLoading(true);
      await custodySettlementService.approve(id);
      toast.success(t.approveSuccess, { position: 'top-center', autoClose: 2000, style: { fontSize: '16px', fontWeight: 'bold' } });
      loadSettlements();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.response?.data || t.approveFailed;
      toast.error(typeof msg === 'string' ? msg : t.approveFailed, { position: 'top-center', autoClose: 5000, style: { fontSize: '16px', fontWeight: 'bold' } });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnapprove = async (id: number) => {
    if (actionLoading) return;
    const ok = await confirm({ message: t.unapproveConfirm, severity: 'warning' });
    if (!ok) return;
    try {
      setActionLoading(true);
      await custodySettlementService.unapprove(id);
      toast.success(t.unapproveSuccess, { position: 'top-center', autoClose: 2000, style: { fontSize: '16px', fontWeight: 'bold' } });
      loadSettlements();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.response?.data || t.unapproveFailed;
      toast.error(typeof msg === 'string' ? msg : t.unapproveFailed, { position: 'top-center', autoClose: 5000, style: { fontSize: '16px', fontWeight: 'bold' } });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdd = () => { setSelectedSettlement(null); setViewOnly(false); setDialogOpen(true); };
  const handleEdit = (s: CustodySettlement) => { setSelectedSettlement(s); setViewOnly(false); setDialogOpen(true); };
  const handleView = (s: CustodySettlement) => { setSelectedSettlement(s); setViewOnly(true); setDialogOpen(true); };
  const handleCloseDialog = () => { setDialogOpen(false); setSelectedSettlement(null); setViewOnly(false); };
  const handlePrint = (s: CustodySettlement) => { setPrintSettlement(s); setPrintOpen(true); };

  const paginatedSettlements = filteredSettlements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getStatusColor = (status?: string): 'default' | 'warning' | 'success' | 'info' => {
    switch (status) {
      case 'Draft': return 'warning';
      case 'Approved': return 'success';
      case 'Posted': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status?: string): string => {
    switch (status) {
      case 'Draft': return t.draft;
      case 'Approved': return t.approved;
      case 'Posted': return t.posted;
      default: return status || '';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount?: number) => `${(amount || 0).toFixed(3)} ${t.kwd}`;

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2.5, mb: 3 }}>
        <Card
          elevation={6}
          sx={{
            background: isDarkMode
              ? 'linear-gradient(135deg, #1a237e 0%, #0d1b3e 100%)'
              : 'linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 24px rgba(92, 107, 192, 0.4)' },
            '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)', pointerEvents: 'none' }
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5, fontWeight: 500 }}>{t.totalSettlements}</Typography>
                <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
              </Box>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1.5, backdropFilter: 'blur(10px)' }}>
                <ReceiptIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card
          elevation={6}
          sx={{
            background: isDarkMode
              ? 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)'
              : 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 24px rgba(255, 167, 38, 0.4)' },
            '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)', pointerEvents: 'none' }
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5, fontWeight: 500 }}>{t.draftSettlements}</Typography>
                <Typography variant="h3" fontWeight="bold">{stats.draft}</Typography>
              </Box>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1.5, backdropFilter: 'blur(10px)' }}>
                <PendingActionsIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card
          elevation={6}
          sx={{
            background: isDarkMode
              ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)'
              : 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 24px rgba(102, 187, 106, 0.4)' },
            '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)', pointerEvents: 'none' }
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5, fontWeight: 500 }}>{t.approvedSettlements}</Typography>
                <Typography variant="h3" fontWeight="bold">{stats.approved}</Typography>
              </Box>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1.5, backdropFilter: 'blur(10px)' }}>
                <DoneIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Header & Filters */}
      <Paper
        elevation={4}
        sx={{
          p: 2.5, mb: 3, borderRadius: 3,
          background: isDarkMode ? alpha(theme.palette.background.paper, 0.6) : 'white',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.1), borderRadius: 2, p: 1.5, display: 'flex' }}>
              <ReceiptIcon sx={{ fontSize: 32, color: '#5c6bc0' }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Tooltip title={t.refresh} arrow>
              <IconButton
                color="primary"
                onClick={loadSettlements}
                sx={{
                  bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.1),
                  '&:hover': { bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.primary.main, 0.2), transform: 'rotate(180deg)' },
                  transition: 'all 0.3s ease',
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {/* ✅ زر إنشاء كشف تسوية — يقبل CUSTODY_VIEW أو CUSTODY_SETTLEMENT_CREATE */}
            {(hasPermission('CUSTODY_VIEW') || hasPermission('CUSTODY_SETTLEMENT_CREATE')) && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                size="large"
                sx={{
                  borderRadius: 2, px: 3,
                  background: 'linear-gradient(45deg, #5c6bc0 30%, #3949ab 90%)',
                  boxShadow: '0 3px 5px 2px rgba(92, 107, 192, .3)',
                  '&:hover': { background: 'linear-gradient(45deg, #4a5ab0 30%, #283593 90%)', boxShadow: '0 5px 8px 3px rgba(92, 107, 192, .4)', transform: 'translateY(-2px)' },
                  transition: 'all 0.3s ease',
                }}
              >
                {t.newSettlement}
              </Button>
            )}
          </Stack>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto auto auto auto' }, gap: 2 }}>
          <TextField
            fullWidth
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#5c6bc0' }} /></InputAdornment> }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 0 0 2px rgba(92, 107, 192, 0.1)' }, '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(92, 107, 192, 0.2)' } } }}
          />
          <TextField
            select
            label={t.status}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          >
            <MenuItem value="">{t.allStatuses}</MenuItem>
            <MenuItem value="Draft">{t.draft}</MenuItem>
            <MenuItem value="Approved">{t.approved}</MenuItem>
            <MenuItem value="Posted">{t.posted}</MenuItem>
          </TextField>
          <Autocomplete
            size="small"
            options={branches}
            getOptionLabel={(o) => isRTL ? (o.branchNameAr || o.branchName) : o.branchName}
            value={branches.find(b => b.branchName === branchFilter) || null}
            onChange={(_, v) => setBranchFilter(v?.branchName || '')}
            renderInput={(params) => <TextField {...params} label={t.branch} />}
            sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.fromDate}
              value={fromDate ? new Date(fromDate) : null}
              onChange={(date) => {
                const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFromDate(newVal);
                if (toDate && newVal && toDate < newVal) setToDate('');
              }}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={toDate ? new Date(toDate) : maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.toDate}
              value={toDate ? new Date(toDate) : null}
              onChange={(date) => {
                const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setToDate(newVal);
                if (fromDate && newVal && fromDate > newVal) setFromDate('');
              }}
              format="dd/MM/yyyy"
              minDate={fromDate ? new Date(fromDate) : minDate}
              maxDate={maxDate}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }}
            />
          </LocalizationProvider>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={4}
        sx={{
          borderRadius: 3, overflow: 'hidden',
          background: isDarkMode ? alpha(theme.palette.background.paper, 0.6) : 'white',
          backdropFilter: 'blur(10px)',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
            <CircularProgress sx={{ color: '#5c6bc0' }} size={60} />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ background: isDarkMode ? 'linear-gradient(135deg, #1a237e 0%, #0d1b3e 100%)' : 'linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }}>{t.settlementNumber}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }}>{t.custody}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }}>{t.settlementDate}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }} align="right">{t.totalExpenses}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }} align="right">{t.returnedAmount}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }}>{t.branch}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }} align="center">{t.status}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 2 }} align="center">{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSettlements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <ReceiptIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />
                      <Typography variant="h6" color="text.secondary">{t.noData}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSettlements.map((settlement, index) => (
                  <TableRow
                    key={settlement.settlementId}
                    hover
                    sx={{
                      '&:hover': { bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.1) : 'rgba(92, 107, 192, 0.05)' },
                      transition: 'background-color 0.2s ease',
                      bgcolor: index % 2 === 0 ? (isDarkMode ? 'transparent' : 'rgba(0,0,0,0.02)') : 'transparent',
                    }}
                  >
                    <TableCell>
                      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 1, px: 1, py: 0.5, display: 'inline-block' }}>
                        <Typography fontWeight="bold" color="primary" variant="body2">{settlement.settlementNumber}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {(settlement as any).custodyNumber} - {isRTL ? (settlement.custodyNameAr || settlement.custodyName) : settlement.custodyName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(settlement.settlementDate)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="error.main">{formatCurrency(settlement.totalExpenses)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="success.main">{formatCurrency(settlement.returnedAmount)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" component="div">{isRTL ? (settlement.branchNameAr || settlement.branchName) : settlement.branchName || '-'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={getStatusLabel(settlement.status)} color={getStatusColor(settlement.status)} size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5, minWidth: 80 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={t.view} arrow>
                          <IconButton size="small" onClick={() => handleView(settlement)}
                            sx={{ color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2), transform: 'scale(1.1)' }, transition: 'all 0.2s ease' }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.print} arrow>
                          <IconButton size="small" onClick={() => handlePrint(settlement)}
                            sx={{ color: '#667eea', bgcolor: alpha('#667eea', 0.1), '&:hover': { bgcolor: alpha('#667eea', 0.2), transform: 'scale(1.1)' }, transition: 'all 0.2s ease' }}>
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* ✅ زر التعديل — يقبل CUSTODY_VIEW أو CUSTODY_SETTLEMENT_CREATE */}
                        {settlement.status === 'Draft' && (hasPermission('CUSTODY_VIEW') || hasPermission('CUSTODY_SETTLEMENT_CREATE')) && (
                          <Tooltip title={t.edit} arrow>
                            <IconButton size="small" onClick={() => handleEdit(settlement)}
                              sx={{ color: 'info.main', bgcolor: alpha(theme.palette.info.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2), transform: 'scale(1.1)' }, transition: 'all 0.2s ease' }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* ✅ زر الاعتماد — يقبل CUSTODY_VIEW أو CUSTODY_SETTLEMENT_APPROVE */}
                        {settlement.status === 'Draft' && (hasPermission('CUSTODY_VIEW') || hasPermission('CUSTODY_SETTLEMENT_APPROVE')) && (
                          <Tooltip title={t.approve} arrow>
                            <IconButton size="small" disabled={actionLoading} onClick={() => handleApprove(settlement.settlementId)}
                              sx={{ color: 'success.main', bgcolor: alpha(theme.palette.success.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2), transform: 'scale(1.1)' }, transition: 'all 0.2s ease' }}>
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* ✅ زر إلغاء الاعتماد — يقبل CUSTODY_VIEW أو CUSTODY_SETTLEMENT_APPROVE */}
                        {settlement.status === 'Approved' && (hasPermission('CUSTODY_VIEW') || hasPermission('CUSTODY_SETTLEMENT_APPROVE')) && (
                          <Tooltip title={t.unapprove} arrow>
                            <IconButton size="small" onClick={() => handleUnapprove(settlement.settlementId)}
                              sx={{ color: 'warning.main', bgcolor: alpha(theme.palette.warning.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.2), transform: 'scale(1.1)' }, transition: 'all 0.2s ease' }}>
                              <UnapproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* ✅ زر الحذف — يقبل CUSTODY_VIEW أو CUSTODY_SETTLEMENT_CREATE */}
                        {settlement.status === 'Draft' && (hasPermission('CUSTODY_VIEW') || hasPermission('CUSTODY_SETTLEMENT_CREATE')) && (
                          <Tooltip title={t.delete} arrow>
                            <IconButton size="small" onClick={() => handleDelete(settlement.settlementId)}
                              sx={{ color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2), transform: 'scale(1.1)' }, transition: 'all 0.2s ease' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={filteredSettlements.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          labelRowsPerPage={t.rowsPerPage}
          labelDisplayedRows={({ from, to, count }) => isRTL ? `${from}-${to} من ${count}` : `${from}-${to} of ${count}`}
          sx={{ borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.4) : 'rgba(0,0,0,0.02)' }}
        />
      </TableContainer>

      {dialogOpen && (
        <CustodySettlementDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          settlement={selectedSettlement || undefined}
          viewOnly={viewOnly}
          onSave={() => { handleCloseDialog(); loadSettlements(); }}
        />
      )}

      {printOpen && (
        <CustodySettlementPreviewDialog
          open={printOpen}
          onClose={() => { setPrintOpen(false); setPrintSettlement(null); }}
          settlement={printSettlement}
        />
      )}
    </Box>
  );
};

export default CustodySettlementsPage;